import { useState, memo, useEffect } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { EuiFilterButton, EuiFlexGroup, EuiFlexItem, EuiToolTip } from "@elastic/eui";
import moment from "moment-timezone";
import { AggregationIntervals } from "Consts";
import FDChartIntervalSelect, { FDChartIntervalSelectProps } from "./ChartIntervalSelect";
import { getDatesParams, getAutoChartInterval } from "utils";
import Popover from "../Popover/Popover";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";

export interface FDTimeSeriesIntervalSelectButtonProps {
  // date picker date
  end: string;
  interval: string;
  onChange: (interval: string) => void;
  // date picker date
  selectProps?: Partial<FDChartIntervalSelectProps>;
  seriesData: any[];
  start: string;
  useGlobalState: boolean;
  disabled?: boolean;
}

const FDTimeSeriesIntervalSelectButton: React.FC<FDTimeSeriesIntervalSelectButtonProps> = ({
  interval,
  useGlobalState = true,
  onChange,
  seriesData = [],
  start = null,
  end = null,
  selectProps = {},
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const [chartInterval, setChartInterval] = useState(interval);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { DatePickerParams } = useSelector(globalFilterSelector, shallowEqual);

  useEffect(() => {
    //validate selected is allowed by date range
    const range = getDatesParams(
      useGlobalState ? DatePickerParams.start : start || DatePickerParams.start,
      useGlobalState ? DatePickerParams.end : end || DatePickerParams.end
    );

    const rangeEnd = moment(range.endDate);
    const rangeStart = moment(range.startDate);
    const allowedOptions = AggregationIntervals.filter((i) => i.isAllowed(rangeStart, rangeEnd));

    if (
      !allowedOptions.some((o) => o.id === chartInterval) // interval is not in the allowed options
    ) {
      if (useGlobalState) {
        console.log(" SelectedPeriod: Auto after " + chartInterval + " not allowed");

        dispatch({
          type: globalFilterTypes.SET_CURRENT_FILTER, // todo: action for date as this is confusing
          payload: {
            SelectedPeriod: "Auto",
          },
        });
      }
      setChartInterval("Auto");
      onChange("Auto");
    }
  }, [chartInterval, DatePickerParams, start, end, useGlobalState]);

  const getTitle = (chartData, chartInterval) => {
    let currentInterval = chartInterval || AggregationIntervals[0].id;
    const autoIntervalCalc = getAutoChartInterval(chartData.map((i) => ({ ...i, x: moment(i.x) })));

    if (currentInterval === "Auto") {
      currentInterval = (
        <EuiFlexGroup alignItems="center" gutterSize="xs" responsive={false}>
          <EuiFlexItem grow={false}>{currentInterval}</EuiFlexItem>
          <EuiFlexItem
            grow={false}
            style={{ width: 28, justifyItems: "center", textAlign: "center" }}>
            {autoIntervalCalc}
          </EuiFlexItem>
        </EuiFlexGroup>
      );
    }
    return currentInterval;
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
  };

  const onIntervalChange = (value: string) => {
    setChartInterval(value);
    onChange(value);
    setIsPopoverOpen(false);
  };

  return (
    <EuiToolTip content={"Chart Interval"}>
      <Popover
        button={
          <EuiFilterButton
            className="chart_interval"
            style={{ color: "var(--text)", width: "115px" }}
            iconSide="left"
            data-testid="chartInterval"
            iconType={"recentlyViewedApp"}
            disabled={disabled}
            onClick={() => {
              setIsPopoverOpen(!isPopoverOpen);
            }}
            title="Chart Interval"
            withNext>
            {getTitle(seriesData, chartInterval)}
          </EuiFilterButton>
        }
        onClose={closePopover}
        isOpen={isPopoverOpen}
        children={
          <FDChartIntervalSelect
            //@ts-ignore
            end={useGlobalState ? null : end}
            //@ts-ignore
            onChange={onIntervalChange}
            //@ts-ignore
            selected={useGlobalState ? null : chartInterval}
            //@ts-ignore
            start={useGlobalState ? null : start}
            useGlobalState={useGlobalState}
            {...selectProps}
          />
        }
      />
    </EuiToolTip>
  );
};

export default memo(FDTimeSeriesIntervalSelectButton);
